import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  shareReplay,
  throwError,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PortRequestFormServiceService {
  private reportDataSubject = new BehaviorSubject<any>(null);
  reportData$ = this.reportDataSubject.asObservable();
  private readonly vesselNamesInflight = new Map<string, Observable<any>>();

  private myRequestsCache$: Observable<any> | null = null;
  private myRequestsCacheTime: number = 0;
  private trackerDataCache$: Observable<any> | null = null;
  private trackerDataCacheTime: number = 0;
  private readonly PC_CACHE_TTL = 60000; // 60s cache TTL for instant tab switches & in-flight deduplication

  clearPortPerformanceFrontendCaches(): void {
    this.myRequestsCache$ = null;
    this.myRequestsCacheTime = 0;
    this.trackerDataCache$ = null;
    this.trackerDataCacheTime = 0;
  }
  getReportTypes() {
    throw new Error('Method not implemented.');
  }
  public readonly baseUrl = environment.apiBasePcUrl;
  private reportDataSource = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) {}

  setReportData(data: any) {
    console.log('Setting reportData:', data);
    this.reportDataSubject.next(data);
  }

  //  get report data (optional)
  getReportData() {
    return this.reportDataSubject.value;
  }

  //  clear report data
  clearReportData(): void {
    this.reportDataSubject.next(null);
  }
  uploadFile(file: File): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/upload-excel`;
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData);
  }
  submitJsonReport(payload: {
    json_report_id: number;
    vesselIMO: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/submit-json-report`;
    return this.http.post(url, payload);
  }
  verifyMasterReport(
    payload: any,
    forceSubmit: boolean = false,
  ): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/verify-master-report${forceSubmit ? '?force_submit=true' : ''}`;
    return this.http.post(url, payload);
  }
  /** Ignore one error of a report (multipart: report_id, field_key, reason, error, files). */
  ignoreReportError(form: FormData): Observable<any> {
    return this.http.post(`${this.baseUrl}/port-consumption/report-errors/ignore`, form);
  }
  getIgnoredReportErrors(reportId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/port-consumption/report-errors/ignored`, { params: { report_id: String(reportId) } });
  }
  ignoredErrorFileUrl(fileId: number): string {
    return `${this.baseUrl}/port-consumption/report-errors/ignored/files/${fileId}`;
  }
  verifyMasterReportIgnoreWarning(
    payload: any,
    forceSubmit: boolean = false,
  ): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/verify-master-report?ignoreWarning=true${forceSubmit ? '&force_submit=true' : ''}`;
    return this.http.post(url, payload);
  }
  //  Set report data
  // setReportData(data: any): void {
  //   this.reportDataSource.next(data);
  // }
  // setReportData(data: any) {
  //   console.log('Setting reportData:', data);
  //   this.reportDataSubject.next(data);
  // }
  // //  Clear shared report data
  // clearReportData(): void {
  //   this.reportDataSource.next(null);
  // }

  //  Submit Master Report API
  submitMasterReport(payload: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/submit-master-report`;

    return this.http.post(url, payload).pipe(
      catchError((error) => {
        console.error('Submit Master Report Error:', error);
        throw error;
      }),
    );
  }

  getPendingReportByIMO(imo: string, id: number): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/get-pending-report?imo=${imo}&id=${id}`;

    return this.http.get<any>(url).pipe(
      catchError((error) => {
        console.error('Get Pending Report Error:', error);
        throw error;
      }),
    );
  }

  downloadErrorExcel(id: number): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/errors-excel?id=${id}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  recordErrorEmail(payload: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/record-error-email`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  previewErrorEmail(payload: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/preview-error-email`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  getLastVoyageData(imo: string): Observable<any> {
    const endpoint = `/port-consumption/get-last-data/${imo}`;

    return this.http.get<any>(`${this.baseUrl}${endpoint}`).pipe(
      catchError((error) => {
        console.error('Get Last Voyage Data Error:', error);
        throw error;
      }),
    );
  }

  getFleetEvents(imoNumber: string, voyageNumber: string): Observable<any> {
    const url = `${this.baseUrl}/fleet/events/${imoNumber}/${voyageNumber}`;

    return this.http.get<any>(url).pipe(
      catchError((error) => {
        console.error('Get Fleet Events Error:', error);
        throw error;
      }),
    );
  }

  getLastSubmittedReport(imo: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/port-consumption/last-submitted-report?imo=${imo}`,
    );
  }

  uploadFiles(excelFile: File | null, pdfFile: File, jsonFile?: File | null): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/upload-vessel-files?excel&pdf`;
    const formData = new FormData();
    if (excelFile) {
      formData.append('excel', excelFile);
    }
    if (pdfFile) {
      formData.append('pdf', pdfFile);
    }
    if (jsonFile) {
      formData.append('json', jsonFile);
    }

    return this.http.post(url, formData);
  }
  uploadMasterReportExcel(file: File) {
    const url = `${this.baseUrl}/upload-master-report`;

    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<any>(url, formData);
  }
  getVesselEvents(imo: any): Observable<any> {
    const url = `${this.baseUrl}/vessel-events/${imo}`;

    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  downloadMasterReportExcel(payload: any) {
    const url = `${this.baseUrl}/scperformance/download_master_report_excel_file`;

    return this.http.post(url, payload, {
      responseType: 'blob',
    });
  }
  PostRequestForm(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/request-submit`;

    return this.http.post(url, jsonData).pipe(
      tap(() => this.clearPortPerformanceFrontendCaches()),
      catchError(this.errorHandler)
    );
  }
  getMyRequests(forceRefresh: boolean = false): Observable<any> {
    const now = Date.now();
    if (!forceRefresh && this.myRequestsCache$ && (now - this.myRequestsCacheTime < this.PC_CACHE_TTL)) {
      return this.myRequestsCache$;
    }
    const url = `${this.baseUrl}/port-consumption/get-port-details`;
    this.myRequestsCacheTime = now;
    this.myRequestsCache$ = this.http.get<any>(url).pipe(
      shareReplay(1),
      catchError((err) => {
        this.myRequestsCache$ = null;
        this.myRequestsCacheTime = 0;
        return this.errorHandler(err);
      })
    );
    return this.myRequestsCache$;
  }
  getPortDetails(): Observable<any> {
    const url = `${this.baseUrl}/combined-port`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getReport(jsonData: any): Observable<Blob> {
    const url = `${this.baseUrl}/get-port-consumption-report`;
    const headers = new HttpHeaders({ 'X-Skip-Ngx-Ui-Loader': 'true' });
    return this.http
      .post(url, jsonData, { responseType: 'blob', headers })
      .pipe(catchError(this.errorHandler));
  }

  getDesignTcpData(mode: any) {
    const url = `${this.baseUrl}/port-consumption/design-tcp-values?mode=${mode}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getIdelManCargoData(mode: any) {
    const url = `${this.baseUrl}/port-consumption/idle-maneuvering-tcp-consumption?mode=${mode}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getActiveInactive(params: {
    client?: string;
    from_date?: string;
    to_date?: string;
    segment?: string;
  }) {
    let queryParams: string[] = [];

    if (params.client) {
      queryParams.push(`client=${encodeURIComponent(params.client)}`);
    }
    if (params.segment) {
      queryParams.push(`segment=${encodeURIComponent(params.segment)}`);
    }
    if (params.from_date) {
      queryParams.push(`from_date=${encodeURIComponent(params.from_date)}`);
    }
    if (params.to_date) {
      queryParams.push(`to_date=${encodeURIComponent(params.to_date)}`);
    }

    const url =
      `${this.baseUrl}/port-consumption/vessel-status` +
      (queryParams.length ? `?${queryParams.join('&')}` : '');

    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getTrackerData(forceRefresh: boolean = false): Observable<any> {
    const now = Date.now();
    if (!forceRefresh && this.trackerDataCache$ && (now - this.trackerDataCacheTime < this.PC_CACHE_TTL)) {
      return this.trackerDataCache$;
    }
    const url = `${this.baseUrl}/get-port-consumption-data`;
    this.trackerDataCacheTime = now;
    this.trackerDataCache$ = this.http.get<any>(url).pipe(
      shareReplay(1),
      catchError((err) => {
        this.trackerDataCache$ = null;
        this.trackerDataCacheTime = 0;
        return this.errorHandler(err);
      })
    );
    return this.trackerDataCache$;
  }

  // Delete a my-request row
  deleteMyRequestRow(payload: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/delete-my-request-row`;
    const token = localStorage.getItem('authToken');
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();

    return this.http.delete(url, { headers, body: payload }).pipe(
      tap(() => this.clearPortPerformanceFrontendCaches()),
      catchError(this.errorHandler)
    );
  }

  // Delete a database row from Database tab
  deleteDatabaseRow(payload: any): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/delete-database-row`;
    const token = localStorage.getItem('authToken');
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();

    return this.http.delete(url, { headers, body: payload }).pipe(
      tap(() => this.clearPortPerformanceFrontendCaches()),
      catchError(this.errorHandler)
    );
  }

  getPortConsumptionClients(status: boolean) {
    const url = `${this.baseUrl}/port-consumption/clients?status=${status}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getPortConsumptionFilterOptions(payload: Record<string, unknown>) {
    const url = `${this.baseUrl}/port-consumption/filter-options`;
    return this.http
      .post<any>(url, payload)
      .pipe(catchError(this.errorHandler));
  }
  getFleetViewData(Status: any) {
    const url = `${this.baseUrl}/fleet-view?status=${Status}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getVesselViewData(shipName: any, imo: any) {
    const url = `${this.baseUrl}/vessel-view?vessel_name=${shipName}&vessel_imo=${imo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getOnboardingVesselsData() {
    const url = `${this.baseUrl}/port-consumption/vessel-onboarding`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getOnboardingVesselsDetailData(imo: any) {
    const url = `${this.baseUrl}/port-consumption/vessel-onboarding-details?vessel_imo=${imo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getQ88VesselsData() {
    const url = `${this.baseUrl}/port-consumption/q88/`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  downloadQ88Pdf(endpoint: string) {
    const finalUrl = `https://portconsumption.merakishippingservices.com${endpoint}`;

    return this.http.get<any>(finalUrl).pipe(catchError(this.errorHandler));
  }
  quickOnboardingByImo(imo: number): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/quick-onboarding`;

    return this.http
      .post<any>(url, { imo_number: imo })
      .pipe(catchError(this.errorHandler));
  }

  getVesselList(): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/q88/`;

    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getQ88VesselsDetailData(imo: any) {
    const cleanImo = imo?.toString().trim();
    const url = `${this.baseUrl}/port-consumption/q88/${cleanImo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  PostClaimableAmount(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/update-claim-data`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getPorts(): Observable<any> {
    const url = `${this.baseUrl}/port`;
    return this.http.get(url).pipe(catchError(this.errorHandler));
  }
  getAllSegments(): Observable<any> {
    const url = `${this.baseUrl}/get-segment`;
    return this.http.get(url).pipe(catchError(this.errorHandler));
  }
  getSegmentsName(): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_segments`;
    return this.http.post(url, {}).pipe(catchError(this.errorHandler));
  }
  getBenchmarkSegments(payload: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_segments`;
    return this.http.post(url, payload);
  }
  getSegmentsBnch(payload: any): Observable<any> {
    const url = `${this.baseUrl}/tcp_benchmarking_segments`;
    return this.http.post(url, payload);
  }
  getSegmentsByClient(client: string): Observable<any> {
    const url = `${this.baseUrl}/get-segment?client=${client}`;
    return this.http.get(url).pipe(catchError(this.errorHandler));
  }

  getTcpBenchmarking(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/tcp_bench_marking`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getPcBenchmarking(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/pc_bench_marking`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  private normalizeImo(value: unknown): string | null {
    const cleaned = String(value ?? '')
      .trim()
      .replace(/[^0-9]/g, '');

    return cleaned.length ? cleaned : null;
  }

  getCombinedExcelData(source: string, imo: string): Observable<string> {
    const normalizedImo = this.normalizeImo(imo);

    if (!normalizedImo) {
      return throwError(() => new Error('IMO number is required to generate the combined report.'));
    }

    const url = `${this.baseUrl}/combined_get_data`;
    return this.http
      .get(url, {
        params: { source, imo_number: normalizedImo },
        responseType: 'text',
      })
      .pipe(catchError(this.errorHandler));
  }
  getReportJson(imo: string): Observable<string> {
    const url = `${this.baseUrl}/port-consumption/get-pending-report`;
    return this.http
      .get(url, {
        params: { imo },
        responseType: 'text',
      })
      .pipe(catchError(this.errorHandler));
  }

  getUserManual() {
    const url = `${this.baseUrl}/port-consumption/get-portconsumption-file`;
    return this.http
      .get(url, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }
  getReportVerify() {
    const url = `${this.baseUrl}/port-consumption/get-pending-report`;

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        const reports =
          response?.vessels?.flatMap((v: any) => v.reports || []) || [];

        return reports.map((report: any) => ({
          id: report.id,
          vesselName: report.vesselName,
          vesselIMO: report.vesselIMO,
          createdAt: report.createdAt,
          emailReceivedAt: report.emailReceivedAt,
          jsonCreatedAt: report.jsonCreatedAt,
          eventsList: report.eventsList,
          vesselSegment: report.vesselSegment,
          vesselType: report.vesselType,
        }));
      }),
      catchError(this.errorHandler),
    );
  }
  downloadMasterReportPc(jsonData: any): Observable<Blob> {
    const url = `${this.baseUrl}/scperformance/download_master_report_excel_file_pc`;

    return this.http
      .post(url, jsonData, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }

  updateStatus(body: any) {
    const url = `${this.baseUrl}/update-active-inactive`;
    return this.http
      .post(url, body, { responseType: 'text' })
      .pipe(catchError(this.errorHandler));
  }
  updateRemarks(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/add-reamarks-data`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getVessels() {
    return this.http.get<any>(`${this.baseUrl}/port-consumption/vessels`);
  }

  getVesselNames(jsonData: { segment: string[] }): Observable<{
    status: string;
    segment: string[];
    vessel_name: string[];
  }> {
    const segments = this.normalizeVesselNameSegments(jsonData?.segment);

    if (!segments.length) {
      return of({
        status: 'success',
        segment: [],
        vessel_name: [],
      });
    }

    const cacheKey = segments.join('|');
    const inflight = this.vesselNamesInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const request$ = this.fetchVesselNames(segments).pipe(
      finalize(() => {
        this.vesselNamesInflight.delete(cacheKey);
      }),
      shareReplay(1),
    );

    this.vesselNamesInflight.set(cacheKey, request$);
    return request$;
  }

  private normalizeVesselNameSegments(segments: unknown): string[] {
    return [
      ...new Set(
        (Array.isArray(segments) ? segments : [segments])
          .flat()
          .map((segment) => String(segment).toUpperCase().trim())
          .filter(Boolean),
      ),
    ].sort();
  }

  private fetchVesselNames(segments: string[]): Observable<{
    status: string;
    segment: string[];
    vessel_name: string[];
  }> {
    const url = `${this.baseUrl}/vessel-name`;

    return this.http.post(url, { segment: segments }).pipe(
      map((response) => this.parseVesselNameApiResponse(response, segments)),
      catchError(() =>
        of({
          status: 'error',
          segment: segments,
          vessel_name: [],
        }),
      ),
    );
  }

  private parseVesselNameApiResponse(
    raw: unknown,
    requestSegments: string[],
  ): {
    status: string;
    segment: string[];
    vessel_name: string[];
    vessel_imo?: Record<string, string | number>;
  } {
    const body = raw as {
      status?: string;
      segment?: string[];
      vessel_name?: unknown[];
      vessel_imo?: unknown;
    };

    const vesselNames: string[] = [];
    const vesselImoByName: Record<string, string | number> = {};

    const addImoForName = (name: string, imo: unknown): void => {
      const trimmedName = String(name).trim();
      const imoDigits = String(imo ?? '')
        .replace(/\D/g, '')
        .trim();
      if (trimmedName && imoDigits) {
        vesselImoByName[trimmedName] = imoDigits;
      }
    };

    if (Array.isArray(body?.vessel_name)) {
      body.vessel_name.forEach((entry) => {
        if (typeof entry === 'string') {
          const name = entry.trim();
          if (name) {
            vesselNames.push(name);
          }
          return;
        }

        if (!entry || typeof entry !== 'object') {
          return;
        }

        const record = entry as Record<string, unknown>;
        const name = String(
          record['vessel_name'] ?? record['vesselName'] ?? record['name'] ?? '',
        ).trim();
        if (!name) {
          return;
        }

        vesselNames.push(name);
        const embeddedImo =
          record['vessel_imo'] ??
          record['vesselIMO'] ??
          record['imo'] ??
          record['IMO'] ??
          record['imo_number'] ??
          record['imoNumber'];
        if (
          embeddedImo !== undefined &&
          embeddedImo !== null &&
          embeddedImo !== ''
        ) {
          addImoForName(name, embeddedImo);
        }
      });
    }

    if (Array.isArray(body?.vessel_imo)) {
      body.vessel_imo.forEach((imo, index) => {
        const name = vesselNames[index];
        if (name) {
          addImoForName(name, imo);
        }
      });
    } else if (
      body?.vessel_imo &&
      typeof body.vessel_imo === 'object' &&
      !Array.isArray(body.vessel_imo)
    ) {
      Object.entries(body.vessel_imo as Record<string, unknown>).forEach(
        ([name, imo]) => addImoForName(name, imo),
      );
    }

    return {
      status: body?.status ?? 'success',
      segment: Array.isArray(body?.segment) ? body.segment : requestSegments,
      vessel_name: vesselNames,
      vessel_imo: Object.keys(vesselImoByName).length
        ? vesselImoByName
        : undefined,
    };
  }
  getVoyageList(imo: string) {
    return this.http.get<any>(`${this.baseUrl}/voyage-list/${imo}`);
  }

  getVoyageReports(imo: string, voyageNumber: string) {
    return this.http.get<any>(
      `${this.baseUrl}/voyage-reports?imo=${imo}&voyage_number=${voyageNumber}`,
    );
  }
  getVoyageReportsByDate(
    imo: string,
    fromDate: string,
    toDate: string,
    reportType?: string,
  ) {
    let url = `${this.baseUrl}/voyage-reports?imo=${imo}&from_date=${fromDate}&to_date=${toDate}`;

    if (reportType && reportType.trim() !== '') {
      url += `&passage_type=${reportType}`;
    }
    return this.http.get<any>(url);
  }
  getReportsList(
    imo: string,
    fromDate: string,
    toDate: string,
    p0: string,
    reportType?: string,
  ) {
    let url = `${this.baseUrl}/reports-list?imo=${imo}&from_date=${fromDate}&to_date=${toDate}&page=1&limit=50`;
    if (reportType && reportType.trim() !== '') {
      url += `&passage_type=${reportType}`;
    }
    return this.http.get<any>(url);
  }

  getVoyageReport(imo: string, voyageNumber: string, reportType?: string) {
    let url = `${this.baseUrl}/api/voyage-reports?imo=${imo}&voyage_number=${voyageNumber}`;

    if (reportType) {
      url += `&type=${reportType}`;
    }

    return this.http.get<any>(url);
  }
  getReportsByVessel(imo: string) {
    return this.http.get<any[]>(
      `${this.baseUrl}/port-consumption/vessel/${imo}/reports`,
    );
  }

  getReportsByVesselAndDate(imo: string, date: string) {
    return this.http.get<any[]>(
      `${this.baseUrl}/port-consumption/vessel/${imo}/reports/${date}`,
    );
  }
  // OPEN: json_reports.verified = false + delete the linked master_reports row
  openJsonReport(payload: { json_report_id: number; vesselIMO: string }) {
    return this.http.post<any>(
      `${this.baseUrl}/port-consumption/json-report/open`,
      payload,
    );
  }

  // OVERWRITE: replace the report with an uploaded vessel JSON (any event / time) as its next
  // version: master_reports rows deleted, old versions verified = false + is_active = false
  overwriteJsonReport(file: File, payload: { json_report_id: number; vesselIMO: string }) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('json_report_id', String(payload.json_report_id));
    formData.append('vesselIMO', payload.vesselIMO);

    return this.http.post<any>(
      `${this.baseUrl}/port-consumption/json-report/overwrite`,
      formData,
    );
  }

  // DELETE-CHECK: check if json report can be deleted or must be overwritten
  checkCanDeleteJsonReport(payload: { json_report_id: number; vesselIMO: string }) {
    return this.http.post<any>(
      `${this.baseUrl}/port-consumption/json-report/delete-check`,
      payload,
    );
  }

  // DELETE: delete json report and linked master rows if not linked to report desk
  deleteJsonReport(payload: { json_report_id: number; vesselIMO: string }) {
    return this.http.post<any>(
      `${this.baseUrl}/port-consumption/json-report/delete`,
      payload,
    );
  }


  getPendingReports(imo: string, id: number) {
    return this.http.get<any>(
      `${this.baseUrl}/port-consumption/get-pending-report`,
      { params: { imo, id } },
    );
  }

  updatePort(payload: {
    json_report_id: number;
    vesselIMO: string;
    voyageNumber?: string;
    field: 'departurePort' | 'destinationPort';
    oldValue: string;
    newName: string;
    propagate: boolean;
    addToMaster: boolean;
    latitude?: number;
    longitude?: number;
  }): Observable<any> {
    const url = `${this.baseUrl}/port-consumption/update-port`;
    return this.http.post(url, payload);
  }

  getVcFleetView(imo: any) {
    const url = `${this.baseUrl}/fleet/${imo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getPastVoyagesData(imo: any, date: any, condition: any, endDate: any) {
    const url = `${this.baseUrl}/fleet/${imo}?date=${date}&vesselCondition=${condition}&endDate=${endDate}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getNotGeneratedVoyages(imo: any) {
    return this.http.get<any[]>(`${this.baseUrl}/vessel-passages?imo=${imo}`);
  }
  getVesselPosition() {
    return this.http.get<any[]>(
      `${this.baseUrl}/port-consumption/vessel-position`,
    );
  }
  getCombinedReportData(imo: string, source?: 'PC' | 'SNC', vesselName?: string) {
    const normalizedImo = this.normalizeImo(imo);

    if (!normalizedImo) {
      return throwError(() => new Error('IMO number is required to download the combined report.'));
    }

    let url = `${this.baseUrl}/combined_get_data?imo_number=${encodeURIComponent(normalizedImo)}`;

    if (source) {
      url += `&source=${encodeURIComponent(source)}`;
    }

    if (vesselName && vesselName.trim()) {
      url += `&vessel_name=${encodeURIComponent(vesselName.trim())}`;
    }

    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(this.errorHandler),
    );
  }
  getMap() {
    return this.http.get<any[]>(`${this.baseUrl}/fleet/map`, {
      headers: { 'X-Skip-Ngx-Ui-Loader': 'true' },
    });
  }
  getSegments(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/segments`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getOperations(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_operations`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getOptimizationPorts(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_ports`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getOptimizationOperations(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/operations`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getOptimizationNamePorts(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/ports`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getVesselPositionVoyage(imo: string) {
    return this.http.get(
      `${this.baseUrl}/port-consumption/vessel-position/${imo}`,
    );
  }
  getAverageCargoQty(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/average-cargo-qty`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getOptimization(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/port-optimization`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getVesselPortOptimizationData(payload: {
    vessel_names?: string[];
    vessel_imos?: string[];
    ports?: string[];
  }): Observable<any> {
    const url = `${this.baseUrl}/port-optimization/vessel-port-data`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }

  /**
   * Creates a Voyage View row from Optimization screenshot "Yes" flow.
   * Uses scperformance fleet-view (not portconsumption /vessel-view).
   */
  createVesselViewOptimizationRow(
    payload: Record<string, unknown>,
  ): Observable<any> {
    const url = `${environment.apiBaseUrl}/scperformance/fleet-view`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }

  /**
   * Updates Optimization Consumption (manoeuvring / cargo_ops / idle)
   * for a vessel port call on Port Performance vessel-view.
   */
  updateVesselViewOptimizationConsumption(
    payload: Record<string, unknown>,
  ): Observable<any> {
    const url = `${this.baseUrl}/vessel-view/update-optimization-consumption`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }

  getPortOptimizationBaseline(jsonData: {
    vessel_name: string;
    load_discharge_rate: number;
    expected_cargo_qty: number;
  }): Observable<any> {
    const url = `${this.baseUrl}/port-optimization-baseline`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }

  getPortPerformanceAnalysis(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/port-performance-analysis`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getTpGraphs(
    imo: any,
    graph_id: any,
    from_date: any,
    to_date: any,
    condition: any,
    bf_filter?: string,
  ) {
    let url = `${this.baseUrl}/vessel/technical-performance/graphs?imo=${imo}&graph_id=${graph_id}&from_date=${from_date}&to_date=${to_date}&condition=${condition}`;
    if (bf_filter) {
      url += `&bf_filter=${bf_filter}`;
    }
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  downloadLiveFuelModelExcel(imo: string, voyage_id?: string): Observable<any> {
    let url = `${this.baseUrl}/vessel/live-fuel-model/download-excel?imo=${imo}`;
    if (voyage_id) {
      url += `&voyage_id=${encodeURIComponent(voyage_id)}`;
    }
    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
    }).pipe(catchError(this.errorHandler));
  }
  getLiveFuelModel(imo: string, from_date: string, to_date: string, condition: string) {
    const url = `${this.baseUrl}/vessel/live-fuel-model?imo=${imo}&from_date=${from_date}&to_date=${to_date}&condition=${condition}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getTpReferenceCurves(imo: string) {
    const url = `${this.baseUrl}/vessel/technical-performance/graphs?imo=${imo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getDigitalTwinComparison(imo: string, model: string = '1984') {
    const url = `${this.baseUrl}/vessel/sea-trial-vs-holtrop?imo=${imo}&model=${model}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  downloadSeaTrialVsHoltropSingle(imo: string, model: string = '1984'): Observable<any> {
    const url = `${this.baseUrl}/vessel/sea-trial-vs-holtrop/download?imo=${imo}&model=${model}`;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(catchError(this.errorHandler));
  }
  downloadSeaTrialVsHoltropAll(model: string = '1984'): Observable<any> {
    const url = `${this.baseUrl}/vessel/sea-trial-vs-holtrop/download-all?model=${model}`;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(catchError(this.errorHandler));
  }
  downloadSeaTrialDocument(imo: string): Observable<any> {
    const url = `${this.baseUrl}/vessel/document/sea-trial/download?imo=${imo}&json=true`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getVesselDocumentData(imo: string, docType: string): Observable<any> {
    const url = `${this.baseUrl}/vessel/document/data?imo=${imo}&doc_type=${docType}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getbenchMarkSegments(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_segments`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getbenchMarkOperations(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_operations`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getbenchMarkPorts(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/benchmarking_ports`;

    return this.http.post(url, jsonData).pipe(catchError(this.errorHandler));
  }
  getTechnicalPerformance() {
    const url = `${this.baseUrl}/technical-performance?status=True`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getFleetLodSummary() {
    const url = `${this.baseUrl}/vessel/technical-performance/fleet-lod`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getCharterPerformance(status: boolean | string = true) {
    const url = `${this.baseUrl}/charter-party-performance?status=${status}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getTechnicalPerformanceView(vesselName: any, vesselImo: any) {
    const url = `${this.baseUrl}/technical-performance-view?vessel_name=${vesselName}&vessel_imo=${vesselImo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getCharterPerformanceView(vesselName: any, vesselImo: any) {
    const url = `${this.baseUrl}/charter-party-performance-view?vessel_name=${vesselName}&vessel_imo=${vesselImo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  quickOnboarding(imoNumber: any) {
    const url = `${this.baseUrl}/port-consumption/quick-onboarding`;

    const body = {
      imo_number: imoNumber,
    };

    return this.http.post<any>(url, body).pipe(catchError(this.errorHandler));
  }

  /** Vessel master list (Looker Studio) — used by PC / routing forms. */
  getLookerStudioVessels(
    username: string = 'Meraki - Operations',
  ): Observable<any[]> {
    const url = `${environment.apiBaseUrl}/scperformance/looker-studio-vessels`;
    const params = new HttpParams().set('username', username);
    const headers = new HttpHeaders().set('X-Skip-Ngx-Ui-Loader', 'true');
    return this.http.get(url, { params, headers }).pipe(
      map((res: any) =>
        Array.isArray(res) ? res : (res?.vessels ?? res?.data ?? []),
      ),
      catchError(this.errorHandler),
    );
  }
  getOptimizationCompliance(urlParams: any = {}) {
    let params = new HttpParams();

    if (urlParams) {
      Object.keys(urlParams).forEach((key) => {
        if (urlParams[key] !== null && urlParams[key] !== undefined) {
          params = params.set(key, urlParams[key]);
        }
      });
    }

    const url = `${this.baseUrl}/get-port-actual-optimized-consumption-summary`;

    return this.http
      .get<any>(url, { params })
      .pipe(catchError(this.errorHandler));
  }
  getVesselCalibrationDetails(imo: any): Observable<any> {
    const url = `${this.baseUrl}/vessel/calibration-details`;
    const params = new HttpParams().set('imo', String(imo));
    return this.http
      .get<any>(url, { params })
      .pipe(catchError(this.errorHandler));
  }

  uploadAndParseDocument(file: File,docType: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/scperformance/upload_document_parser`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('doc_type', docType);

    return this.http.post<any>(url, formData).pipe(
      catchError(this.errorHandler)
    );
  }


confirmParsedDocument(payload: { file_id: string; parsed_data: any; doc_type?: string }): Observable<any> {
    const url = `${environment.apiBaseUrl}/scperformance/confirm_parsed_document`;

    return this.http.post<any>(url, payload).pipe(
      tap(() => this.clearPortPerformanceFrontendCaches()),
      catchError(this.errorHandler)
    );
  }
  errorHandler(error: HttpErrorResponse) {
    return throwError(() => error || 'Server Error');
  }
}
